/**
 * Fingerprint.js Integration
 *
 * Provides accurate visitor identification using browser fingerprinting.
 * The Fingerprint API key is configured via environment variables at build time.
 */

import FingerprintJS, { type Agent } from '@fingerprintjs/fingerprintjs-pro'

import { env } from './utils/env'

/** Storage key for visitor ID */
const VISITOR_ID_STORAGE_KEY = 'busie_visitor_id'
/** Storage key for visitor confidence score */
const VISITOR_CONFIDENCE_STORAGE_KEY = 'busie_visitor_confidence'

// Cache the fingerprint agent to avoid multiple initializations
let fingerprintAgent: Agent | null = null
let fingerprintAgentPromise: Promise<Agent> | null = null

/**
 * Get or create the Fingerprint.js agent
 * Uses the API key from environment variables
 */
async function getFingerprintAgent(): Promise<Agent> {
  // Return cached agent if available
  if (fingerprintAgent) return fingerprintAgent

  // Return in-progress initialization if one exists
  if (fingerprintAgentPromise) return fingerprintAgentPromise

  if (!env.fingerprint.apiKey) {
    throw new Error('[Busie SDK] Fingerprint API key not configured in environment')
  }

  // Start loading and cache the promise to prevent concurrent loads
  fingerprintAgentPromise = FingerprintJS.load({
    apiKey: env.fingerprint.apiKey,
    // Optional: Configure region if needed
    // region: 'us', // 'us', 'eu', or 'ap'
  })

  try {
    fingerprintAgent = await fingerprintAgentPromise
    return fingerprintAgent
  } catch (error) {
    // Clear the promise on failure so retries are possible
    fingerprintAgentPromise = null
    throw error
  }
}

/**
 * Generate a fallback visitor ID using localStorage + random UUID
 */
function generateFallbackVisitorId(): string {
  try {
    const stored = localStorage.getItem(VISITOR_ID_STORAGE_KEY)
    if (stored) return stored

    const newId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `visitor-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

    localStorage.setItem(VISITOR_ID_STORAGE_KEY, newId)
    return newId
  } catch {
    // localStorage not available (e.g., private browsing in some browsers)
    return `visitor-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }
}

/**
 * Get visitor ID using Fingerprint.js
 * Falls back to localStorage-based ID if Fingerprint fails or is disabled
 *
 * @param fallbackId - Optional fallback ID to use instead of generating one
 * @returns Promise resolving to a visitor ID string
 */
export async function getVisitorId(
  organizationApiKey: string,
  fallbackId?: string
): Promise<string> {
  // If Fingerprint is disabled or no API key configured, use fallback
  if (!env.fingerprint.enabled || !env.fingerprint.apiKey) {
    if (!env.fingerprint.apiKey && env.fingerprint.enabled) {
      console.warn('[Busie SDK] No Fingerprint API key configured, using fallback visitor ID')
    }
    return fallbackId || generateFallbackVisitorId()
  }

  try {
    const agent = await getFingerprintAgent()
    const result = await agent.get({
      // ORGANIZATION API KEY
      linkedId: organizationApiKey,
      tag: { apiKey: organizationApiKey },
      extendedResult: true,
    })

    // Store in localStorage as backup
    try {
      localStorage.setItem(VISITOR_ID_STORAGE_KEY, result.visitorId)
      localStorage.setItem(VISITOR_CONFIDENCE_STORAGE_KEY, String(result.confidence.score))
    } catch {
      // Ignore localStorage errors
    }

    return result.visitorId
  } catch (error) {
    console.error('[Busie SDK] Fingerprint.js error:', error)
    // Fall back to localStorage-based ID
    return fallbackId || generateFallbackVisitorId()
  }
}

/**
 * Check if Fingerprint.js is configured and enabled
 */
export function isFingerprintEnabled(): boolean {
  return env.fingerprint.enabled && !!env.fingerprint.apiKey
}

/**
 * Reset the fingerprint agent cache (useful for testing)
 */
export function resetFingerprintAgent(): void {
  fingerprintAgent = null
  fingerprintAgentPromise = null
}
